import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { useAuth } from "@/core/auth";
import { IUser } from "@/core/auth/auth";
import { isAdmin } from "@/user/utils";

export interface ImpersonationStatusResponse {
  impersonated_user?: IUser;
}

export const impersonationSessionMutationKey = [
  "impersonation",
  "session",
] as const;

export const impersonationStatusKey = () =>
  ["impersonation", "status"] as const;

export const useImpersonationStatus = (enabled = true) => {
  const api = useApi();
  const { isAuthenticated, token, user, isImpersonating } = useAuth();

  return useQuery({
    queryKey: impersonationStatusKey(),
    queryFn: async ({ signal }): Promise<ImpersonationStatusResponse> =>
      api.get("/admin_impersonation", { signal }).then((res) => res.data),
    enabled:
      enabled &&
      isAuthenticated &&
      !!token &&
      (isAdmin(user?.role) || isImpersonating),
    refetchInterval: 15000, // Poll every 15 seconds
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: false, // Don't retry on failure to prevent infinite loops
  });
};

export const useStartImpersonation = () => {
  const api = useApi();

  return useMutation({
    mutationKey: impersonationSessionMutationKey,
    mutationFn: async (userId: string): Promise<ImpersonationStatusResponse> =>
      api
        .post("/admin_impersonation", { impersonated_user_id: userId })
        .then((res) => res.data),
  });
};

export const useEndImpersonation = () => {
  const api = useApi();

  return useMutation({
    mutationKey: impersonationSessionMutationKey,
    mutationFn: async (): Promise<ImpersonationStatusResponse> =>
      api.delete("/admin_impersonation").then((res) => res.data),
  });
};
